<template lang="pug">
div
  .mb-4
    v-row
      v-col(cols="12", sm="6", md="3")
        v-select(
          v-model="selectedWeek",
          :items="availableWeeks",
          item-text="label",
          item-value="value",
          label="Semana",
          outlined,
          dense,
          @change="fetchSchedule"
        )
  
  v-overlay(:value="loading")
    v-progress-circular(indeterminate, size="64")

  v-alert(type="info", v-if="!loading && !scheduleData" outlined)
    | Este empleado no tiene horarios asignados para la semana seleccionada.
    
  div(v-if="!loading && scheduleData")
    // Tarjetas de resumen
    v-row.mb-5
      v-col(cols="12", sm="6", md="4")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Horas Semanales
            .text-h4 {{ totalHours }}h
      v-col(cols="12", sm="6", md="4")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Días Trabajados
            .text-h4 {{ workDays }}
      v-col(cols="12", sm="6", md="4")
        v-card(outlined)
          v-card-text
            .text-overline.mb-1 Promedio Diario
            .text-h4 {{ workDays > 0 ? (totalHours / workDays).toFixed(1) : 0 }}h
  
    // Tabla de horarios semanales
    v-simple-table.schedule-table
      template(v-slot:default)
        thead
          tr
            th Día
            th Turno
            th Horas
        tbody
          template(v-for="day in weekDays")
            tr(v-if="!hasShiftsForDay(day.value)", :key="`${day.value}-empty`")
              td {{ day.label }}
              td.text-center -
              td.text-center -
            template(v-else, v-for="(shift, index) in getShiftsForDay(day.value)")
              tr(:key="`${day.value}-${index}`", :class="{'first-shift': index === 0}")
                td(v-if="index === 0", :rowspan="getShiftsForDay(day.value).length") {{ day.label }}
                td {{ formatShift(shift) }}
                td.text-center {{ calculateHours(shift) }}h
</template>

<script>
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns'
import { es } from 'date-fns/locale'
import { mapActions } from 'vuex'

export default {
  name: 'EmployeeScheduleView',
  
  props: {
    employeeId: {
      type: String,
      required: true
    }
  },
  
  data: () => ({
    loading: false,
    scheduleData: null,
    selectedWeek: new Date().toISOString(),
    availableWeeks: [],
    
    weekDays: [
      { value: 'monday', label: 'Lunes' },
      { value: 'tuesday', label: 'Martes' },
      { value: 'wednesday', label: 'Miércoles' },
      { value: 'thursday', label: 'Jueves' },
      { value: 'friday', label: 'Viernes' },
      { value: 'saturday', label: 'Sábado' },
      { value: 'sunday', label: 'Domingo' }
    ]
  }),
  
  computed: {
    totalHours() {
      if (!this.scheduleData) {
        console.log('No hay datos de horario disponibles');
        return 0;
      }
      
      console.log('Calculando horas para scheduleData:', this.scheduleData);
      let total = 0;
      this.weekDays.forEach(day => {
        const shifts = this.getShiftsForDay(day.value);
        console.log(`Shifts para ${day.label}:`, shifts);
        shifts.forEach(shift => {
          const hours = this.calculateHours(shift);
          console.log(`Horas para turno ${this.formatShift(shift)}:`, hours);
          total += hours;
        });
      });
      
      return total.toFixed(1);
    },
    
    workDays() {
      if (!this.scheduleData) return 0
      
      let count = 0
      this.weekDays.forEach(day => {
        if (this.hasShiftsForDay(day.value)) {
          count++
        }
      })
      
      return count
    }
  },
  
  methods: {
    ...mapActions({
      fetchEmployeeSchedule: 'employeeFiles/fetchEmployeeSchedule'
    }),
    
    generateAvailableWeeks() {
      const today = new Date()
      const weeks = []
      
      // Generamos 8 semanas anteriores y 4 futuras
      for (let i = -8; i <= 4; i++) {
        const weekDate = i === 0 ? today : (i < 0 ? subWeeks(today, Math.abs(i)) : addWeeks(today, i))
        const weekStart = startOfWeek(weekDate, { weekStartsOn: 1 }) // Lunes
        const weekEnd = endOfWeek(weekDate, { weekStartsOn: 1 }) // Domingo
        
        weeks.push({
          value: weekStart.toISOString(),
          label: `${format(weekStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM', { locale: es })}`
        })
      }
      
      this.availableWeeks = weeks
    },
    
    async fetchSchedule() {
      this.loading = true
      
      try {
        const data = await this.fetchEmployeeSchedule(this.selectedWeek)
        console.log('Datos recibidos:', data);
        console.log('Buscando empleado con ID:', this.employeeId);
        
        // Encontrar los datos de este empleado específico
        const employeeSchedule = data.find(schedule => {
          if (!schedule.employee) return false;
          
          // Convertir ambos a string para comparación segura
          const scheduleEmployeeId = schedule.employee._id.toString();
          const currentEmployeeId = this.employeeId.toString();
          
          console.log('Comparando:', scheduleEmployeeId, currentEmployeeId);
          return scheduleEmployeeId === currentEmployeeId;
        });
        
        console.log('Schedule encontrado:', employeeSchedule);
        this.scheduleData = employeeSchedule || null;
      } catch (error) {
        console.error('Error al cargar los horarios:', error)
        this.$toast.error('Error al cargar los horarios')
      } finally {
        this.loading = false
      }
    },
    
    hasShiftsForDay(day) {
      if (!this.scheduleData || !this.scheduleData.shifts || !this.scheduleData.shifts[day]) {
        return false
      }
      
      return this.scheduleData.shifts[day].length > 0
    },
    
    getShiftsForDay(day) {
      if (!this.scheduleData || !this.scheduleData.shifts || !this.scheduleData.shifts[day]) {
        return []
      }
      
      return this.scheduleData.shifts[day]
    },
    
    formatShift(shift) {
      return `${shift.startTime} - ${shift.endTime}`
    },
    
    calculateHours(shift) {
      const [startHour, startMinute] = shift.startTime.split(':').map(Number)
      const [endHour, endMinute] = shift.endTime.split(':').map(Number)
      
      let hours = endHour - startHour
      let minutes = endMinute - startMinute
      
      if (minutes < 0) {
        hours--
        minutes += 60
      }
      
      // Si el turno termina antes de lo que empieza (por ejemplo, de 22:00 a 02:00)
      if (hours < 0) {
        hours += 24
      }
      
      return parseFloat((hours + minutes / 60).toFixed(2))
    }
  },
  
  created() {
    this.generateAvailableWeeks()
    this.fetchSchedule()
  }
}
</script>

<style scoped>
.schedule-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.first-shift {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.v-card {
  transition: all 0.3s;
}

.v-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
}
</style>
